import React from 'react';
import { motion } from 'framer-motion';
import {
  Camera, Briefcase, MessageSquare, CreditCard, CalendarCheck,
  KeyRound, Instagram, Mail, Activity, ShieldAlert, Bot, Gift, Facebook,
} from 'lucide-react';

/*
 * SeeItInAction — 14 real Telegram case-study screenshots.
 * All are production emails delivered to TelegramEmailBot users
 * across Security, OTP, Newsletters, HR, Hospitality and Loyalty.
 */

const SHOTS = [
  // ─── Security Alerts (NEW category) ───
  {
    src: "/telegram-shots/facebook-security.jpeg",
    icon: <ShieldAlert className="w-4 h-4" />,
    tag: "Security Alert",
    caption: "Facebook Login Alert",
    desc: "\"Did you just log in near Port Harcourt on a new device?\" — real Meta security email with map, device, timestamp — piped instantly to Telegram.",
    accent: "from-red-600 to-orange-600",
  },
  {
    src: "/telegram-shots/xai-login.jpeg",
    icon: <Bot className="w-4 h-4" />,
    tag: "Security Alert",
    caption: "xAI New Login Notification",
    desc: "xAI notifies you the second a new device logs into your Grok account — IP, location, browser — you see it before the intruder does anything.",
    accent: "from-gray-800 to-black",
  },
  // ─── CCTV / IT / Uptime ───
  {
    src: "/telegram-shots/cctv-alert.jpeg",
    icon: <Camera className="w-4 h-4" />,
    tag: "CCTV / Security",
    caption: "Motion Detection Alert",
    desc: "Real customer's IP camera fired 'Motion Detection' with a live snapshot — delivered to Telegram in seconds.",
    accent: "from-rose-500 to-orange-500",
  },
  {
    src: "/telegram-shots/uptime-monitoring.jpeg",
    icon: <Activity className="w-4 h-4" />,
    tag: "Uptime / IT",
    caption: "Server Down Alert",
    desc: "PingPlotter's 'Guest Internet — Eastlink Down!' with packet-loss graph attached, straight to the on-call Telegram group.",
    accent: "from-red-500 to-rose-600",
  },
  // ─── Verification Codes / OTP ───
  {
    src: "/telegram-shots/x-verification.jpeg",
    icon: <KeyRound className="w-4 h-4" />,
    tag: "Verification Code",
    caption: "X (Twitter) OTP",
    desc: "New X signup? The 6-digit verification code lands in Telegram in under 3 seconds — no need to open Gmail.",
    accent: "from-gray-900 to-slate-700",
  },
  {
    src: "/telegram-shots/instagram-verification.jpeg",
    icon: <Instagram className="w-4 h-4" />,
    tag: "Verification Code",
    caption: "Instagram OTP",
    desc: "Instagram signup code sent to abc@telegramemailbot.com and delivered to Telegram in real time.",
    accent: "from-fuchsia-500 via-pink-500 to-orange-500",
  },
  {
    src: "/telegram-shots/facebook-code.jpeg",
    icon: <Facebook className="w-4 h-4" />,
    tag: "Verification Code",
    caption: "Facebook Confirm Email",
    desc: "\"014457 is your code to confirm this email\" — Meta's email verification arrives instantly in Telegram, code ready to paste.",
    accent: "from-[#1877F2] to-blue-600",
  },
  {
    src: "/telegram-shots/wwt-verify.jpeg",
    icon: <KeyRound className="w-4 h-4" />,
    tag: "Enterprise / B2B",
    caption: "WWT Account Verification",
    desc: "World Wide Technology enterprise account verification — B2B / SaaS onboarding emails hit Telegram before your laptop even boots.",
    accent: "from-blue-800 to-indigo-900",
  },
  {
    src: "/telegram-shots/facebook-welcome.jpeg",
    icon: <Facebook className="w-4 h-4" />,
    tag: "Onboarding",
    caption: "Facebook Welcome Email",
    desc: "\"Thanks for signing up to Facebook mobile!\" — Meta's welcome emails, download links and next-step tips forwarded intact to Telegram.",
    accent: "from-[#1877F2] to-sky-500",
  },
  // ─── SaaS Newsletters / Loyalty ───
  {
    src: "/telegram-shots/saas-newsletter.jpeg",
    icon: <Mail className="w-4 h-4" />,
    tag: "SaaS / Newsletter",
    caption: "Consensus Product Updates",
    desc: "SaaS product emails (Consensus MCP, ChatGPT, Claude, Codex updates) forwarded to a dedicated Telegram chat — Gmail stays clean.",
    accent: "from-indigo-500 to-violet-600",
  },
  {
    src: "/telegram-shots/consensus-premium.jpeg",
    icon: <Mail className="w-4 h-4" />,
    tag: "SaaS Upsell",
    caption: "Consensus Premium Offer",
    desc: "\"Go further with Consensus Premium\" — subscription upgrade emails, discount codes and pricing offers land in Telegram for quick review.",
    accent: "from-emerald-400 to-teal-500",
  },
  {
    src: "/telegram-shots/calendly-updates.jpeg",
    icon: <Mail className="w-4 h-4" />,
    tag: "SaaS / Newsletter",
    caption: "Calendly Product Updates",
    desc: "\"What's the latest at Calendly?\" — monthly product newsletters routed away from Gmail into Telegram, read on your terms.",
    accent: "from-[#0069FF] to-blue-500",
  },
  {
    src: "/telegram-shots/marriott-bonvoy.jpeg",
    icon: <Gift className="w-4 h-4" />,
    tag: "Loyalty / Rewards",
    caption: "Marriott Bonvoy Newsletter",
    desc: "Marriott Bonvoy \"How to Rack Up Points\" email — full HTML, images intact, Marriott branding preserved perfectly in Telegram.",
    accent: "from-amber-500 to-orange-600",
  },
  // ─── HR / Hospitality (StayMii integrations) ───
  {
    src: "/telegram-shots/hr-application.jpeg",
    icon: <Briefcase className="w-4 h-4" />,
    tag: "HR / Recruiting",
    caption: "Job Application + AI Summary",
    desc: "Candidate submits a form → team gets the application with an AI-generated summary at the top for instant assessment.",
    accent: "from-violet-500 to-fuchsia-500",
  },
  {
    src: "/telegram-shots/booking.jpeg",
    icon: <CalendarCheck className="w-4 h-4" />,
    tag: "Hospitality",
    caption: "Booking Confirmation",
    desc: "New booking → itemized invoice with total, cash and outstanding balance delivered to the front-desk Telegram group.",
    accent: "from-[#0088CC] to-violet-600",
  },
  {
    src: "/telegram-shots/guest-checkin.jpeg",
    icon: <CalendarCheck className="w-4 h-4" />,
    tag: "Hospitality",
    caption: "Guest Check-In Form",
    desc: "StayMii guest self check-in → Unit #, Guest Name, Nationality, ID Type, ID attachment — full registration data streamed to the property team.",
    accent: "from-sky-500 to-blue-600",
  },
  {
    src: "/telegram-shots/guest-checkout.jpeg",
    icon: <CalendarCheck className="w-4 h-4" />,
    tag: "Hospitality",
    caption: "Check-Out & Deposit Refund",
    desc: "Guest submits check-out with bank details + refund proof photos → property team receives 'Refund Required' alert in Telegram to action same-day.",
    accent: "from-orange-500 to-red-500",
  },
  // ─── Product proof ───
  {
    src: "/telegram-shots/stripe-checkout.jpeg",
    icon: <CreditCard className="w-4 h-4" />,
    tag: "Payments",
    caption: "One-Tap Stripe Checkout",
    desc: "Clean $4.99 Starter card with 'Pay via Stripe' button — inline in the chat, no browser hopping.",
    accent: "from-emerald-500 to-teal-500",
  },
  {
    src: "/telegram-shots/bot-help.jpeg",
    icon: <MessageSquare className="w-4 h-4" />,
    tag: "In-Bot Commands",
    caption: "Full Command Menu",
    desc: "/start, /send, /reset, /subscribe, /block, /link — everything runs inside Telegram, zero external app.",
    accent: "from-[#0088CC] to-sky-500",
  },
];

export default function SeeItInAction() {
  return (
    <section className="py-20 sm:py-28 px-4 bg-gray-50/50" data-testid="see-it-in-action">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 shadow-sm rounded-full px-4 py-1.5 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="font-manrope text-xs font-bold text-gray-700 tracking-wide">19 REAL CASE STUDIES · NOT MOCKUPS</span>
          </div>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-gray-900 tracking-tight leading-tight max-w-3xl mx-auto">
            See it in action.{" "}
            <span className="bg-gradient-to-r from-[#0088CC] to-violet-600 bg-clip-text text-transparent">
              Straight from Telegram.
            </span>
          </h2>
          <p className="font-manrope text-base sm:text-lg text-gray-600 max-w-2xl mx-auto mt-5 leading-relaxed">
            Real messages from real customers. Security alerts, verification codes, product newsletters, loyalty programs, HR forms and more — all delivered instantly to Telegram.
          </p>
        </div>

        {/* Gallery — 1-col mobile → 2-col tablet → 3-col desktop → 4-col XL */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {SHOTS.map((s, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04, duration: 0.5 }}
              className="group relative bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-[#0088CC]/40 hover:shadow-[0_20px_50px_-20px_rgba(0,136,204,0.28)] hover:-translate-y-1 transition-all duration-300"
              data-testid={`screenshot-${i}`}
            >
              {/* Tag pill */}
              <div className="absolute top-3 left-3 z-10">
                <span className={`inline-flex items-center gap-1.5 bg-gradient-to-r ${s.accent} text-white font-manrope font-bold text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md`}>
                  {s.icon}
                  {s.tag}
                </span>
              </div>

              {/* Screenshot */}
              <div className="aspect-[9/16] bg-gray-50 overflow-hidden">
                <img
                  src={s.src}
                  alt={`${s.caption} — real TelegramEmailBot screenshot`}
                  loading="lazy"
                  className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                />
              </div>

              {/* Caption */}
              <figcaption className="p-5">
                <h3 className="font-outfit font-bold text-sm text-gray-900 leading-tight mb-1.5">
                  {s.caption}
                </h3>
                <p className="font-manrope text-xs text-gray-600 leading-relaxed">
                  {s.desc}
                </p>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {/* Footer note */}
        <p className="text-center font-manrope text-xs text-gray-400 mt-8">
          Some personal information redacted. All screenshots are from live production accounts.
        </p>
      </div>
    </section>
  );
}
